// Spec 45 component-behavior — cmp_ds_chip.
//
// Compact, optionally-removable token surface (selected filter, tag,
// person picker entry). Two interaction modes:
//
//   * static (default): non-interactive. Renders as <span>.
//   * removable: includes a built-in close affordance. Renders as
//     <span role="group" aria-label="{label}"> wrapping the visible
//     label + a <button aria-label="Remove {label}"> for the close.
//     Keyboard removal happens via the close button (focusable +
//     native Enter/Space activation); we don't pile a second
//     Backspace/Delete handler onto the wrapper because that turns
//     the wrapper into a non-native interactive element (jsx-a11y/
//     no-static-element-interactions). List-context callers can
//     wire chip-list-level Backspace removal themselves.
//
// Spec 49 voice rule: the close-button accessible name is "Remove
// {label}" — concrete object, not a generic "Remove".

import { forwardRef, type HTMLAttributes, type MouseEvent } from "react";

import { cx } from "../lib/cx.ts";

export type ChipTone = "neutral" | "accent" | "info" | "success" | "warning" | "danger";

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: ChipTone;
  /** Visible label — also used to build the close button's accessible name when removable. */
  readonly label: string;
  /** When set, renders the close affordance. Fired when the user clicks the close button. */
  readonly onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ tone = "neutral", label, onRemove, className, ...rest }, ref) => {
    const removable = onRemove !== undefined;
    return (
      <span
        ref={ref}
        data-component="chip"
        data-tone={tone}
        data-removable={removable || undefined}
        // Removable chip is a logical group containing a label + a
        // close button. role=group + aria-label gives the group an
        // accessible name without making the wrapper itself interactive.
        role={removable ? "group" : undefined}
        aria-label={removable ? label : undefined}
        className={cx("nai-chip", className)}
        {...rest}
      >
        <span data-component="chip-label" className="nai-chip-label">
          {label}
        </span>
        {removable && (
          <button
            type="button"
            data-component="chip-remove"
            aria-label={`Remove ${label}`}
            className="nai-chip-remove"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(event);
            }}
          >
            ×
          </button>
        )}
      </span>
    );
  },
);
Chip.displayName = "Chip";
